import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FocusModeWarnings} from 'contexts/FocusMode';

/**
 * Minimum effective time (in seconds) for the 50% warning to be shown.
 * If 50% of the total time is less than 10 minutes, the 50% warning is skipped
 * so the student doesn't receive two warnings in quick succession.
 */
const MIN_SECONDS_FOR_50_WARNING = 1200; // 20 minutes total → 50% = 10 min

const WARNING_MESSAGES = {
  TEN_MINUTES: 'Atenção! Você tem 10 minutos restantes.',
  HALF_TIME: (minutes: number) =>
    `Atenção! O tempo está acabando. Você tem ${minutes} ${
      minutes === 1 ? 'minuto' : 'minutos'
    } restantes.`,
} as const;

export interface UseFocusModeWarningsParams {
  isActive: boolean;
  remainingSeconds: number;
  effectiveTimeSeconds: number;
  warnings: FocusModeWarnings;
  setWarnings: (partial: Partial<FocusModeWarnings>) => void;
  sessionInitialRemaining: number | null;
}

/**
 * useFocusModeWarnings
 *
 * Monitors remaining time during Focus Mode and surfaces time warnings.
 * Warnings shown:
 *   - 10-min warning: shown once when remainingSeconds ≤ 600 s (highest priority).
 *   - 50% warning: shown once when remainingSeconds ≤ 50% of effectiveTimeSeconds,
 *     but only when effectiveTimeSeconds ≥ 20 min (otherwise the 50% threshold
 *     would overlap with the 10-min warning).
 *
 * Both warnings are shown at most once per session (controlled by the
 * warning50Shown / warning10Shown flags in FocusModeContext).
 *
 * Returns `warningMessage` (string | null) and `dismissWarning()` so the
 * caller can render a modal and close it.
 */
function useFocusModeWarnings({
  isActive,
  remainingSeconds,
  effectiveTimeSeconds,
  warnings,
  setWarnings,
  sessionInitialRemaining,
}: UseFocusModeWarningsParams) {
  const [activeWarning, setActiveWarning] = useState<string | null>(null);

  // Track if session started above each threshold
  const startedAbove10MinRef = useRef(false);
  const startedAbove50PercentRef = useRef(false);

  const dismissWarning = useCallback(() => {
    setActiveWarning(null);
  }, []);

  const halfTime = useMemo(
    () => Math.floor(effectiveTimeSeconds / 2),
    [effectiveTimeSeconds],
  );

  // Mark if session started above each threshold
  useEffect(() => {
    if (sessionInitialRemaining === null) {
      startedAbove10MinRef.current = false;
      startedAbove50PercentRef.current = false;
      return;
    }

    startedAbove10MinRef.current = sessionInitialRemaining > 600;
    startedAbove50PercentRef.current = sessionInitialRemaining > halfTime;
  }, [sessionInitialRemaining, halfTime]);

  useEffect(() => {
    if (!isActive) {
      startedAbove10MinRef.current = false;
      startedAbove50PercentRef.current = false;
      setActiveWarning(null);
    }
  }, [isActive]);

  // Monitor remaining time and show warnings
  useEffect(() => {
    if (!isActive || effectiveTimeSeconds <= 0) {
      setActiveWarning(null);
      return;
    }

    const remainingMinutes = Math.ceil(remainingSeconds / 60);

    // 10-minute warning
    if (
      startedAbove10MinRef.current &&
      remainingSeconds <= 600 &&
      !warnings.warning10Shown
    ) {
      setWarnings({warning10Shown: true});
      setActiveWarning(WARNING_MESSAGES.TEN_MINUTES);
      return;
    }

    // 50% warning
    if (
      startedAbove50PercentRef.current &&
      remainingSeconds <= halfTime &&
      !warnings.warning50Shown &&
      effectiveTimeSeconds >= MIN_SECONDS_FOR_50_WARNING
    ) {
      setWarnings({warning50Shown: true});
      setActiveWarning(WARNING_MESSAGES.HALF_TIME(remainingMinutes));
      return;
    }
  }, [
    isActive,
    remainingSeconds,
    effectiveTimeSeconds,
    halfTime,
    setWarnings,
    warnings.warning10Shown,
    warnings.warning50Shown,
  ]);

  return {warningMessage: activeWarning, dismissWarning};
}

export default useFocusModeWarnings;
